import { useQuery } from '@apollo/client';
import { useEffect, useMemo } from 'react';
import { buildAccountFromViewer, GET_VIEWER_QUERY, ViewerQueryResult } from '../services/account';
import { onUserIdentified } from '../services/user-events';

// Identify once per fetched viewer result (matching the legacy
// requestCurrentAccount behavior), not once per consuming component.
let lastIdentifiedViewer: unknown;

// Called on sign-out so the next sign-in on this tab re-identifies and no
// reference to the previous account's viewer is retained.
export const resetIdentifiedViewer = () => {
  lastIdentifiedViewer = undefined;
};

// The signed-in account/subscription, served from the Apollo cache. The
// query is fetched once per app session (cache-first) and explicitly
// refetched by mutations that change account data.
export const useAccount = () => {
  const { data, loading, error, refetch } = useQuery<ViewerQueryResult>(GET_VIEWER_QUERY, {
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (data?.viewer && data.viewer !== lastIdentifiedViewer) {
      lastIdentifiedViewer = data.viewer;
      onUserIdentified(data.viewer);
    }
  }, [data]);

  const account = useMemo(
    () => (data?.viewer ? buildAccountFromViewer(data.viewer) : undefined),
    [data]
  );

  return { account, loading, error, refetch };
};
